import { useEffect } from 'react';
import { APP_NAME, PUBLIC_SITE_ORIGIN } from '../../constants/ApplicationConstants';

type SchemaValue = Record<string, unknown> | Array<Record<string, unknown>>;

type PageSeoProps = {
  title: string;
  description: string;
  robots?: string;
  canonicalPath?: string | null;
  imagePath?: string;
  schema?: SchemaValue | null;
  alternates?: Array<{
    hrefLang: string;
    href: string;
  }>;
};

const TITLE_SUFFIX = ` | ${APP_NAME}`;

const upsertMeta = (attributeName: 'name' | 'property', attributeValue: string, content: string) => {
  let element = document.head.querySelector<HTMLMetaElement>(`meta[${attributeName}="${attributeValue}"]`);
  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(attributeName, attributeValue);
    document.head.appendChild(element);
  }
  element.setAttribute('content', content);
};

const upsertLink = (rel: string, href: string) => {
  let element = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!element) {
    element = document.createElement('link');
    element.setAttribute('rel', rel);
    document.head.appendChild(element);
  }
  element.setAttribute('href', href);
};

const removeLink = (rel: string) => {
  const element = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (element) {
    element.remove();
  }
};

const syncAlternateLinks = (alternates: Array<{ hrefLang: string; href: string }>) => {
  const existingElements = Array.from(
    document.head.querySelectorAll<HTMLLinkElement>('link[rel="alternate"][data-lovibook-seo-alternate="true"]'),
  );

  existingElements.forEach((element) => element.remove());

  alternates.forEach((alternate) => {
    const element = document.createElement('link');
    element.rel = 'alternate';
    element.hreflang = alternate.hrefLang;
    element.href = alternate.href;
    element.dataset.lovibookSeoAlternate = 'true';
    document.head.appendChild(element);
  });
};

const resolveAbsoluteUrl = (path?: string) => {
  if (!path) {
    return PUBLIC_SITE_ORIGIN;
  }

  return new URL(path, PUBLIC_SITE_ORIGIN).toString();
};

const PageSeo = ({
  title,
  description,
  robots = 'index, follow',
  canonicalPath,
  imagePath = '/logo.png?v=20260422c',
  schema = null,
  alternates = [],
}: PageSeoProps) => {
  useEffect(() => {
    document.title = title.endsWith(TITLE_SUFFIX) ? title : `${title}${TITLE_SUFFIX}`;

    upsertMeta('name', 'description', description);
    upsertMeta('name', 'robots', robots);
    upsertMeta('property', 'og:type', 'website');
    upsertMeta('property', 'og:site_name', APP_NAME);
    upsertMeta('property', 'og:title', title);
    upsertMeta('property', 'og:description', description);
    upsertMeta('property', 'og:image', resolveAbsoluteUrl(imagePath));
    upsertMeta('name', 'twitter:card', 'summary_large_image');
    upsertMeta('name', 'twitter:title', title);
    upsertMeta('name', 'twitter:description', description);
    upsertMeta('name', 'twitter:image', resolveAbsoluteUrl(imagePath));

    if (canonicalPath) {
      const canonicalUrl = resolveAbsoluteUrl(canonicalPath);
      upsertLink('canonical', canonicalUrl);
      upsertMeta('property', 'og:url', canonicalUrl);
    } else {
      removeLink('canonical');
    }

    syncAlternateLinks(alternates);

    const existingSchema = document.head.querySelector<HTMLScriptElement>('script[data-lovibook-seo-schema="true"]');
    if (schema) {
      const nextSchema = existingSchema ?? document.createElement('script');
      nextSchema.type = 'application/ld+json';
      nextSchema.dataset.lovibookSeoSchema = 'true';
      nextSchema.textContent = JSON.stringify(schema);
      if (!existingSchema) {
        document.head.appendChild(nextSchema);
      }
    } else if (existingSchema) {
      existingSchema.remove();
    }
  }, [alternates, canonicalPath, description, imagePath, robots, schema, title]);

  return null;
};

export default PageSeo;
